// Behaviour dispatch (MOB-2) + the §27.6 script fall-through. PURE (ENG-1).
//
// Resolves the per-tick behaviour for a mob: a per-name override (creeper/spider/
// zombie/wolf/snow_golem/sheep special files) when one exists, otherwise the plain
// archetype module. Keeping this table out of entity.ts breaks the entity ↔
// archetype import cycle.
//
// §27.8's SCHEDULE (M45.5) is not decided here. `dispatchMobHook` hands the runtime the
// four facts only the entity layer knows — which entity, which type, which tick, which
// `scriptHz` — and the runtime applies the cadence, the frame ceiling, the quarantine
// ladder and the per-dispatch rng. All three refusals come back as an ordinary
// `{ ok: false }`, so an off-beat, deferred or quarantined mob falls through to its
// archetype by the SAME path a script error does, and this function needs no new case.
//
// SCRIPTS RUN FIRST, AND FALL THROUGH (ARCH §27.6, SCR-7). A mob carrying
// `MobDef.script` dispatches its hook BEFORE `behaviorFor(type, archetype)`, and the
// archetype **still runs afterwards** unless the script called `handled()`. That is the
// whole design: a script ADDS behaviour, it does not replace it, and `handled()` is
// good for exactly one dispatch — a fresh `EntityMobHost` per dispatch is what makes
// "never sticky" structural rather than a flag somebody has to remember to clear.
// A script that ABORTS suppresses nothing: §27.8's ladder says a broken script falls
// back to its archetype, so a `handled()` that ran before the abort is discarded.
//
// The `ScriptMobHost` adapter lives HERE, beside the table, for the same reason the
// table does: it needs the MOB-3 steering helpers (`steerToward`, `tickSpeed`), and
// importing those into `entity.ts` is precisely the cycle this file exists to break.
// It is also the §27.6 boundary made physical — the adapter can reach `wishX`/`wishZ`/
// `wishJump`, the entity's own `scriptMem`, its yaw, its target and the system's
// damage path, and there is no member on it that writes `pos` or `vel`.
import { MOB } from '../config';
import type { MobDef } from '../registry/schemas';
import { ScriptMissing, type MobHook, type MobScriptRuntime } from '../script/system';
import type { MobMemValue, MobPath, MobSteering, ScriptHost, ScriptMobHost } from '../script/api';
import {
  halfOf, heightOf, scriptMemGet, scriptMemSet,
  type Entity, type MobContext, type EntitySystem, type MobFrame,
} from './entity';
import { steerToward } from './pathfind/steer';
import { tickSpeed } from './archetypes/shared';
import { grazer } from './archetypes/grazer';
import { melee } from './archetypes/melee';
import { ranged } from './archetypes/ranged';
import { flying } from './archetypes/flying';
import { tamable } from './archetypes/tamable';
import { golem } from './archetypes/golem';
import { ambient } from './archetypes/ambient';
import { fish } from './archetypes/fish';
import { villager } from './archetypes/villager';
import { creeper } from './mobs/creeper';
import { zombie } from './mobs/zombie';
import { spider } from './mobs/spider';
import { wolf } from './mobs/wolf';
import { snowGolem } from './mobs/snow_golem';
import { sheep } from './mobs/sheep';
import { enderman } from './mobs/enderman';
import { slime, slimeDeath } from './mobs/slime';
import { piglin } from './mobs/piglin';
import { wither } from './mobs/wither';
import { warden } from './mobs/warden';
import { enderDragon } from './mobs/ender_dragon';

export type Behavior = (e: Entity, def: MobDef, ctx: MobContext) => void;
export type DeathHandler = (e: Entity, sys: EntitySystem, frame: MobFrame) => void;

const ARCHETYPES: Record<MobDef['archetype'], Behavior> = { grazer, melee, ranged, flying, tamable, golem, ambient, villager, fish };

/** Per-name overrides that replace (and internally reuse) the base archetype. The
 *  magma_cube reuses the slime bounce + split (MB.6 nether hostiles). */
const OVERRIDES: Record<string, Behavior> = { creeper, zombie, spider, wolf, snow_golem: snowGolem, sheep, enderman, slime, magma_cube: slime, piglin, wither, warden, ender_dragon: enderDragon };

/** Per-name death hooks (sized gel-cube split), run from EntitySystem.kill. */
const DEATHS: Record<string, DeathHandler> = { slime: slimeDeath, magma_cube: slimeDeath };

export function behaviorFor(type: string, archetype: MobDef['archetype']): Behavior {
  return OVERRIDES[type] ?? ARCHETYPES[archetype];
}

export function deathFor(type: string): DeathHandler | undefined {
  return DEATHS[type];
}

// ── The §27.6 mob-script layer ────────────────────────────────────────────────

/** The mob-script wiring, injected by the host onto `EntitySystem.mobScripts`.
 *  OPTIONAL and absent by default: with no binding every mob runs exactly its
 *  archetype, which is pre-M45 behaviour and the §27.19 parity claim. */
export interface MobScripts {
  /** The runtime that resolves a script NAME, compiles it for the mob slice and runs
   *  one hook (`sim/script/system.ts`, or a double in a test). */
  system: MobScriptRuntime;
  /** The whitelisted world/effect verb surface every mob dispatch runs against — the
   *  same `ScriptHost` a script block gets, since §27.5 slices the TABLE, not the host. */
  host: ScriptHost;
  /** The world's mob seed — §27.8's `fork(mobSeed, entityId)` parent for the
   *  per-dispatch `random`/`chance` stream. Wiring the host sets once, not per-tick
   *  state; omitted, every world shares one stream, which is still deterministic but
   *  no longer world-specific. */
  seed?: number;
  /** Where E-50's warning goes: a per-instance `scriptOverride` naming a script nothing
   *  provides any more. A host log (server console, browser console) — never chat, since
   *  the player who sees chat is rarely the author. Omitted ⇒ the fallback still happens,
   *  silently. Called at most once per entity per dangling name. */
  warn?: (message: string) => void;
}

/** The script NAME an entity runs (§27.13): its per-instance override when it has one,
 *  else its type's `MobDef.script`, else none. The one place that precedence is written,
 *  so the hook gates, the dispatcher and a host's signal-target scan cannot disagree. */
export function scriptNameOf(e: Entity, def: MobDef): string | undefined {
  return e.scriptOverride ?? def.script;
}

/** Run one §27.6 hook for `e` and report whether the script SUPPRESSED the fall-through.
 *
 *  Never throws (E-30): `dispatchMob` hands every failure back as a result, and a
 *  failure — no source for the name, a refused compile, a runtime abort — reports
 *  `false`, so the mob falls back to its archetype for that dispatch. */
/** One entity's `self.*`/`mem.*` surface, built OUTSIDE an archetype dispatch.
 *
 *  §27.10's signals are delivered by the cascade drain, which §27.4 places in the
 *  `blockEntities` phase — not inside `dispatchMobHook`, which is where the only other
 *  `EntityMobHost` is built. A host delivering a signal therefore needs a surface for a
 *  mob it is not otherwise ticking, and it must be the SAME surface, or a signal would
 *  reach a different set of verbs than a hook does.
 *
 *  One per delivery, like one per dispatch: `handled()` writes `suppressed`, and a
 *  shared host would let one entity's fall-through decision leak into another's. */
export function mobHostFor(e: Entity, def: MobDef, ctx: MobContext): ScriptMobHost {
  return new EntityMobHost(e, def, ctx);
}

export function dispatchMobHook(e: Entity, def: MobDef, ctx: MobContext, hook: MobHook, scripts: MobScripts): boolean {
  const override = e.scriptOverride;
  if (override !== undefined) {
    // §27.13: the entity's own script, instead of its type's. Quarantined under its own
    // ladder key (`type@script`), because §27.8's per-TYPE quarantine exists for the
    // type's shared script — one boss sheep's broken override must not send every
    // sheep to its archetype, and the two scripts must not reset each other's count.
    const ran = runScript(e, def, ctx, hook, scripts, override, `${e.type}@${override}`);
    if (!ran.missing) return ran.suppressed;
    // E-50: the override names a script nothing provides any more. Fall back to the
    // TYPE's script — the entity keeps behaving — and say so once, not five times a
    // second. The name stays on the entity: an author restoring the script restores
    // the override too, with nothing to re-attach.
    if (e.scriptOverrideWarned !== override) {
      e.scriptOverrideWarned = override;
      scripts.warn?.(`entity ${e.id} (${e.type}): script override '${override}' no longer exists — running ${def.script === undefined ? 'its default behaviour' : `the ${e.type} script '${def.script}'`} instead`);
    }
  }
  const script = def.script;
  if (script === undefined) return false;
  return runScript(e, def, ctx, hook, scripts, script, e.type).suppressed;
}

/** One dispatch of `script` for `e`. `missing` ⇒ nothing provides that name (so a
 *  caller holding a fallback can use it); `suppressed` ⇒ the script called `handled()`. */
function runScript(
  e: Entity, def: MobDef, ctx: MobContext, hook: MobHook, scripts: MobScripts, script: string, type: string,
): { suppressed: boolean; missing: boolean } {
  // One host per dispatch: `handled()` cannot outlive the dispatch that called it.
  const mob = new EntityMobHost(e, def, ctx);
  // §27.8's four schedule facts. They are read straight off the entity, its def and the
  // frame — nothing is cached between dispatches, so there is no copy of `scriptHz` or
  // of a tick that could go stale against a reload or a `/time set`. The runtime owns
  // every counter; this layer owns none.
  const res = scripts.system.dispatchMob(script, hook, {
    host: scripts.host,
    mob,
    entityId: e.id,
    type,
    hz: def.scriptHz,
    tick: ctx.frame.tick,
    seed: scripts.seed,
  });
  return { suppressed: res.ok && mob.suppressed, missing: res.error instanceof ScriptMissing };
}

/** One entity's `self.*` / `mem.*` / `handled()` surface for ONE dispatch (§27.5's mob
 *  slice, implemented against §27.6's rules).
 *
 *  Pose is readable and NOT writable — `x/y/z` are getters over the live entity because
 *  `moveAway`/`faceAt` need the mob's own position, and there is no setter for them, for
 *  velocity, or for anything else physics owns. Movement leaves through `steering`,
 *  which writes the MOB-3 AI scratch the archetypes already write. */
class EntityMobHost implements ScriptMobHost {
  /** Set by `handled()` (§27.6's fall-through switch), read once by the dispatcher. */
  suppressed = false;
  readonly steering: MobSteering;

  constructor(
    private readonly e: Entity,
    private readonly def: MobDef,
    private readonly ctx: MobContext,
  ) {
    // Accessors over the entity rather than a copy: a script's steering IS the AI
    // scratch, so there is no window in which the two could disagree, and `stepPhysics`
    // consumes exactly what the script wrote (or what the archetype wrote over it).
    const ent = e;
    this.steering = {
      get wishX(): number { return ent.wishX; },
      set wishX(v: number) { ent.wishX = v; },
      get wishZ(): number { return ent.wishZ; },
      set wishZ(v: number) { ent.wishZ = v; },
      get wishJump(): boolean { return ent.wishJump; },
      set wishJump(v: boolean) { ent.wishJump = v; },
    };
  }

  get x(): number { return this.e.x; }
  get y(): number { return this.e.y; }
  get z(): number { return this.e.z; }

  /** MOB-3 greedy steering toward a horizontal goal — the same `steerToward` every
   *  archetype uses, with the same water avoidance, so a script cannot walk a mob
   *  through a wall, off a cliff or into lava. The hop is gated on standing on the
   *  ground here (the side that can read `onGround`), exactly as `moveToward` does. */
  pathToward(goalX: number, goalZ: number): MobPath {
    const { e } = this;
    const r = steerToward(this.ctx.world, e.x, e.y, e.z, goalX, goalZ, { avoidFluid: e.avoidsWater });
    return { dx: r.dx, dz: r.dz, jump: r.jump && e.onGround };
  }

  /** Blocks/tick, from the registry stat — so a script cannot outrun `MobDef.speed`. */
  moveSpeed(): number { return tickSpeed(this.def); }

  face(yaw: number): void { this.e.yaw = yaw; }

  /** Target a live entity. Refuses an unknown id and refuses the mob itself, so a
   *  script cannot arrange for a mob to attack its own id. */
  setTarget(id: number): boolean {
    const target = this.ctx.sys.byId(id);
    if (!target || target.id === this.e.id) return false;
    this.e.targetEntityId = id;
    return true;
  }

  clearTarget(): void {
    this.e.targetEntityId = -1;
    this.e.targetIdx = -1;
  }

  /** Strike the current target with the mob's OWN `attackDamage` on its OWN cooldown
   *  (MOB-5 melee). False when there is no damage stat, no live target, the target is
   *  out of reach, or the cooldown is unspent — a script can ask to attack as often as
   *  it likes and still only land a mob's normal rate of hits. */
  attack(): boolean {
    const { e, def, ctx } = this;
    if (!def.attackDamage || e.attackCooldown > 0) return false;
    const target = ctx.sys.byId(e.targetEntityId);
    if (!target) return false;
    const targetDef = ctx.sys.def(target.type);
    if (!targetDef) return false;
    // MOB.MELEE.REACH_PAD is "extra reach beyond the two AABB half-widths"; vertically
    // the two boxes must actually overlap, so reach is the mobs' own sizes, not a
    // constant anyone has to tune.
    const reach = halfOf(e, def) + halfOf(target, targetDef) + MOB.MELEE.REACH_PAD;
    if (Math.hypot(target.x - e.x, target.z - e.z) > reach) return false;
    if (target.y >= e.y + heightOf(e, def) || target.y + heightOf(target, targetDef) <= e.y) return false;
    ctx.sys.damage(target, def.attackDamage, ctx.frame, target.x - e.x, target.z - e.z, false);
    e.attackCooldown = MOB.MELEE.COOLDOWN;
    return true;
  }

  /** Self-damage through the ordinary damage path, so a script-inflicted death rolls
   *  loot, fires `on death` and removes the mob exactly as any other death does. */
  hurt(amount: number): void {
    this.ctx.sys.damage(this.e, amount, this.ctx.frame);
  }

  /** Heal, clamped to `maxHealth` — the host's clamp, not the script's (§27.5). */
  heal(amount: number): void {
    const { e } = this;
    e.health = Math.min(e.maxHealth, e.health + Math.max(0, amount));
  }

  /** The MOB-4 despawn path: the mob is gone, but it did not DIE — no loot, no XP and
   *  no `on death`, which is what keeps `despawn` from being a loot duplicator. */
  despawn(): void { this.e.removed = true; }

  memGet(key: string): MobMemValue | undefined { return scriptMemGet(this.e, key); }
  memSet(key: string, value: MobMemValue): boolean { return scriptMemSet(this.e, key, value); }

  handled(): void { this.suppressed = true; }
}
